import {
  ContractTaxLine,
  calculateContractAmounts,
} from './calculateContractAmounts'

/** Minutes in a billable day. A day rate buys one of these, however much of it the guest uses. */
export const DAY_MINUTES = 24 * 60

export interface DaySegment {
  readonly durationMinutes: number
  readonly index: number
  /** Minutes from the start of the span, so a caller can date each segment. */
  readonly startOffsetMinutes: number
}

/** Whole days a span covers, which is also its nights: 15:00 Friday to 11:00 Monday is three. */
export const countDays = (durationMinutes: number): number =>
  durationMinutes <= 0 ? 0 : Math.ceil(durationMinutes / DAY_MINUTES)

/**
 * Splits a span of minutes into whole days, measured from its start.
 *
 * The last segment is a full day too — an 11am checkout still costs a night. There is deliberately no
 * partial trailing segment: the only rate fine enough to price a remainder is the flexible ladder, which
 * would put a day-priced span on hourly rates for its last stretch.
 *
 * Measured from the start rather than from midnight, so the count matches the nights the guest holds the
 * resource rather than the calendar dates the span happens to touch.
 */
export const decomposeIntoDailySegments = (
  durationMinutes: number,
): readonly DaySegment[] =>
  Array.from({ length: countDays(durationMinutes) }, (_, index) => ({
    durationMinutes: DAY_MINUTES,
    index,
    startOffsetMinutes: index * DAY_MINUTES,
  }))

/**
 * One tax as it was applied to one segment. The configuration travels with the amount because a folio, a
 * remittance run or a later dispute cannot recover the rate, or whose money it is, from a figure alone.
 */
export interface TaxLineAmount {
  /** What this tax adds to the total. Zero for a tax already inside the price. */
  readonly addedAmount: number
  /** What the tax was computed on, including any taxes it compounds over. */
  readonly baseAmount: number
  readonly computedAmount: number
  /** The configured flat amount charged from, when the tax is a flat fee rather than a rate. */
  readonly appliedFlatAmount?: number
  /** The configured rate charged at, when the tax is a percentage rather than a flat fee. */
  readonly appliedPercentage?: number
  readonly id: string
  readonly includedInPrice: boolean
  /** Whether this is the host's money — a gratuity or host fee — rather than a tax authority's. */
  readonly isHostTax: boolean
}

export interface SegmentAmounts {
  readonly advertisingAmount: number
  readonly breakdown: readonly TaxLineAmount[]
  readonly subtotal: number
  readonly taxAmount: number
  readonly totalAmount: number
}

export interface SegmentedAmounts extends SegmentAmounts {
  /**
   * One entry per segment, in the order given. Kept rather than folded away: they are computed here
   * anyway, and re-deriving them from the aggregate would be a second copy of the folio rule.
   */
  readonly segments: readonly SegmentAmounts[]
}

export interface SegmentedAmountsOptions {
  /** Currency-aware rounding. Defaults to two decimals. */
  round?: (amount: number) => number
  /**
   * How many items the segment's price covers, when the tax set is a property of the item rather than
   * of the booking. Taxes are then computed on one item and multiplied, so a flat fee is charged once
   * per item. Defaults to 1: one tax set for the whole segment.
   */
  taxUnits?: number
}

const roundToCents = (amount: number): number =>
  Math.round((amount + Number.EPSILON) * 100) / 100

const sumBy = <T>(items: readonly T[], pick: (item: T) => number): number =>
  items.reduce((total, item) => total + pick(item), 0)

/**
 * Rolls tax lines sharing an id into one, summing the amounts.
 *
 * Only the amounts merge. The configuration is the same on every date — one tax set applies to the whole
 * line — so it carries through as it stands.
 */
export const mergeTaxLineAmounts = (
  taxLines: readonly TaxLineAmount[],
  round: (amount: number) => number = roundToCents,
): readonly TaxLineAmount[] => [
  ...taxLines
    .reduce<Map<string, TaxLineAmount>>((totals, line) => {
      const running = totals.get(line.id)
      return totals.set(line.id, {
        ...(running ?? line),
        addedAmount: round((running?.addedAmount ?? 0) + line.addedAmount),
        baseAmount: round((running?.baseAmount ?? 0) + line.baseAmount),
        computedAmount: round(
          (running?.computedAmount ?? 0) + line.computedAmount,
        ),
      })
    }, new Map())
    .values(),
]

/**
 * Taxes each segment on its own and sums the results — the hotel folio model, where every night carries
 * its own line.
 *
 * This is what makes a fixed fee behave: a $10 amenity fee across three nights is $30, matching the way
 * a percentage tax already tripled. Taxing the aggregate charged it once.
 *
 * Rounding per segment is what a folio does, and why a total can land a cent away from taxing the
 * aggregate.
 *
 * A segment priced at zero carries no taxes and no fees, matching the rule the charge path states in
 * `BookingAmountsService.computeBookingAmounts`: a booking discounted to nothing costs nothing, flat
 * fees included. Without it a line discounted to zero would still owe its fees.
 *
 * `taxUnits` says how many items the price covers. Above one, the tax set is computed on a single item
 * and multiplied, so a flat fee scales with quantity — three helmets at $10 with a $2 fee owe $6, as
 * `computeAddOnsAmounts` charges them. Percentage taxes land in the same place either way, give or take
 * the cent that per-item rounding moves.
 */
export const calculateSegmentedContractAmounts = (
  segmentPrices: readonly number[],
  taxes: readonly ContractTaxLine[],
  { round = roundToCents, taxUnits = 1 }: SegmentedAmountsOptions = {},
): SegmentedAmounts => {
  // Rounded on entry, so the per-segment rounding holds: three segments of 33.333 total 99.99, not 100.
  const rounded = segmentPrices.map(round)
  const items = taxUnits >= 1 ? taxUnits : 1

  const segments = rounded.map((price): SegmentAmounts => {
    if (price <= 0) {
      return {
        advertisingAmount: price,
        breakdown: [],
        subtotal: price,
        taxAmount: 0,
        totalAmount: price,
      }
    }

    // Taxed one item at a time and scaled back up, so a flat fee is charged per item rather than per
    // segment. With a single item this is the segment's own price, untouched.
    const result = calculateContractAmounts(price / items, [...taxes], {
      round,
    })
    const forAllItems = (amount: number): number => round(amount * items)

    const breakdown = result.breakdown.map(line => ({
      addedAmount: forAllItems(line.addedToTotalAmount),
      appliedFlatAmount: line.appliedFlatAmount,
      appliedPercentage: line.appliedPercentage,
      baseAmount: forAllItems(line.baseAmountForTax),
      computedAmount: forAllItems(line.computedAmount),
      id: line.id,
      includedInPrice: line.includedInPrice,
      isHostTax: line.isHostTax,
    }))

    // Built from the taxes and the segment's own price, not from the per-item total scaled back up: a
    // price that does not divide evenly rounds per item, and 18.97 over two items would come back as
    // 18.98 and book the stray cent as tax.
    const taxAmount = round(sumBy(breakdown, line => line.addedAmount))
    return {
      advertisingAmount: round(
        price +
          sumBy(result.breakdown, line =>
            forAllItems(line.addedToAdvertisingAmount),
          ),
      ),
      breakdown,
      subtotal: price,
      // Only taxes not already inside the price add to what is owed.
      taxAmount,
      totalAmount: round(price + taxAmount),
    }
  })

  return {
    advertisingAmount: round(sumBy(segments, s => s.advertisingAmount)),
    breakdown: mergeTaxLineAmounts(
      segments.flatMap(segment => segment.breakdown),
      round,
    ),
    segments,
    subtotal: round(sumBy(segments, s => s.subtotal)),
    taxAmount: round(sumBy(segments, s => s.taxAmount)),
    totalAmount: round(sumBy(segments, s => s.totalAmount)),
  }
}
